/* eslint-disable @typescript-eslint/no-explicit-any */
// FieldsConfigContext.tsx

import React, {
 createContext,
 ReactNode,
 useCallback,
 useContext,
 useMemo,
 useState,
} from 'react';

import FieldsModal from '../components/common/FieldsModal';

// Define types for dialog state and the context function
interface FieldsConfigProps {
 type: 'sidebar' | 'edit' | null;
 isOpen: boolean;
}

interface FieldsConfigContextType {
 openDialog: ({ type }: Omit<FieldsConfigProps, 'isOpen'>) => void;
}

// Create the context with proper types
const FieldsConfigContext = createContext<FieldsConfigContextType | undefined>(
 undefined,
);

export const FieldsConfigProvider = ({ children }: { children: ReactNode }) => {
 const [dialogState, setDialogState] = useState<FieldsConfigProps>({
  type: null,
  isOpen: false,
 });

 const openDialog = useCallback(
  ({ type }: Omit<FieldsConfigProps, 'isOpen'>) => {
   setDialogState({
    type,
    isOpen: true,
   });
  },
  [],
 );

 const closeDialog = useCallback(() => {
  setDialogState((prevState) => ({ ...prevState, isOpen: false }));
 }, []);

 // Memoize the value for the context provider
 const contextValue = useMemo(
  () => ({
   openDialog,
  }),
  [openDialog],
 );

 return (
  <FieldsConfigContext.Provider value={contextValue}>
   {children}
   {dialogState.isOpen && (
    <FieldsModal
     isOpen={dialogState.isOpen}
     handleClose={closeDialog}
     type={dialogState.type}
    />
   )}
  </FieldsConfigContext.Provider>
 );
};

export const useFieldsConfig = (): FieldsConfigContextType => {
 const context = useContext(FieldsConfigContext);
 if (!context) {
  throw new Error('useFieldsConfig must be used within a FieldsConfigProvider');
 }
 return context;
};
